import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Field, Stat, formatMoney } from '@promtver/ui-kit';
import { ProductionCategory, TicketType } from '@promtver/contracts';

import { getAnalytics, getAnalyticsDirectories } from '../../api/admin';
import { AppShell } from '../../components/AppShell';
import { AsyncState } from '../../components/AsyncState';
import { Select } from '../../components/Select';
import { ru } from '../../i18n/ru';
import { addDaysInputValue, formatPercent, todayInputValue } from '../../lib/datetime';
import { categoryLabel, productionCategories, ticketTypes, ticketTypeLabel } from '../../lib/format';

interface Breakdown {
  title: string;
  items: Array<{ key: string; label: string; value: number }>;
}

/**
 * Сквозная аналитика региона (FR-ADM-01): разрезы по объекту, району,
 * категории, периоду, каналу привлечения, возрасту гостя, типу билета и
 * повторности визита. Данные обезличенные — ПДн не выводятся (FR-OBS-03).
 */
export function AnalyticsPage() {
  const [enterpriseId, setEnterpriseId] = useState('');
  const [districtId, setDistrictId] = useState('');
  const [category, setCategory] = useState('');
  const [from, setFrom] = useState(addDaysInputValue(-30));
  const [to, setTo] = useState(todayInputValue());
  const [channel, setChannel] = useState('');
  const [ageBucket, setAgeBucket] = useState('');
  const [ticketType, setTicketType] = useState('');
  const [repeat, setRepeat] = useState('');

  const directoriesQuery = useQuery({
    queryKey: ['admin', 'analytics-directories'],
    queryFn: getAnalyticsDirectories,
  });

  const analyticsQuery = useQuery({
    queryKey: [
      'admin',
      'analytics',
      enterpriseId,
      districtId,
      category,
      from,
      to,
      channel,
      ageBucket,
      ticketType,
      repeat,
    ],
    queryFn: () =>
      getAnalytics({
        enterpriseId: enterpriseId || undefined,
        districtId: districtId || undefined,
        category: (category || undefined) as ProductionCategory | undefined,
        from,
        to,
        channel: channel || undefined,
        ageBucket: ageBucket || undefined,
        ticketType: (ticketType || undefined) as TicketType | undefined,
        repeat: (repeat || undefined) as 'yes' | 'no' | undefined,
      }),
  });

  const summary = analyticsQuery.data?.summary;

  const breakdowns: Breakdown[] = summary
    ? [
        {
          title: ru.analytics.byDistrict,
          items: summary.byDistrict.map((item) => ({
            key: item.district,
            label: item.district,
            value: item.visits,
          })),
        },
        {
          title: ru.analytics.byCategory,
          items: summary.byCategory.map((item) => ({
            key: item.category,
            label: categoryLabel(item.category),
            value: item.visits,
          })),
        },
        {
          title: ru.analytics.byAge,
          items: summary.byAge.map((item) => ({
            key: item.bucket,
            label: item.bucket,
            value: item.visits,
          })),
        },
        {
          title: ru.analytics.byTicketType,
          items: summary.byTicketType.map((item) => ({
            key: item.ticketType,
            label: ticketTypeLabel(item.ticketType),
            value: item.visits,
          })),
        },
        {
          title: ru.analytics.byChannel,
          items: summary.byChannel.map((item) => ({
            key: item.channel,
            label: item.channel,
            value: item.visits,
          })),
        },
      ]
    : [];

  const reset = () => {
    setEnterpriseId('');
    setDistrictId('');
    setCategory('');
    setChannel('');
    setAgeBucket('');
    setTicketType('');
    setRepeat('');
    setFrom(addDaysInputValue(-30));
    setTo(todayInputValue());
  };

  return (
    <AppShell title={ru.analytics.title} subtitle={ru.analytics.subtitle}>
      <Card title={ru.analytics.filters}>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Select
            label={ru.analytics.objectFilter}
            value={enterpriseId}
            onChange={setEnterpriseId}
            options={[
              { value: '', label: ru.common.all },
              ...(directoriesQuery.data?.enterprises ?? []).map((item) => ({
                value: item.id,
                label: item.name,
              })),
            ]}
          />
          <Select
            label={ru.analytics.districtFilter}
            value={districtId}
            onChange={setDistrictId}
            options={[
              { value: '', label: ru.common.all },
              ...(directoriesQuery.data?.districts ?? []).map((item) => ({
                value: item.id,
                label: item.name,
              })),
            ]}
          />
          <Select
            label={ru.analytics.categoryFilter}
            value={category}
            onChange={setCategory}
            options={[
              { value: '', label: ru.common.all },
              ...productionCategories.map((item) => ({ value: item, label: categoryLabel(item) })),
            ]}
          />
          <Select
            label={ru.analytics.channelFilter}
            value={channel}
            onChange={setChannel}
            options={[
              { value: '', label: ru.common.all },
              ...(directoriesQuery.data?.channels ?? []).map((item) => ({ value: item, label: item })),
            ]}
          />
          <Select
            label={ru.analytics.ageFilter}
            value={ageBucket}
            onChange={setAgeBucket}
            options={[
              { value: '', label: ru.common.all },
              ...(directoriesQuery.data?.ageBuckets ?? []).map((item) => ({
                value: item,
                label: item,
              })),
            ]}
          />
          <Select
            label={ru.analytics.ticketFilter}
            value={ticketType}
            onChange={setTicketType}
            options={[
              { value: '', label: ru.common.all },
              ...ticketTypes.map((item) => ({ value: item, label: ticketTypeLabel(item) })),
            ]}
          />
          <Select
            label={ru.analytics.repeatFilter}
            value={repeat}
            onChange={setRepeat}
            options={[
              { value: '', label: ru.common.all },
              { value: 'yes', label: ru.analytics.repeatYes },
              { value: 'no', label: ru.analytics.repeatNo },
            ]}
          />
          <Field label={ru.common.from} value={from} onChange={setFrom} type="date" required />
          <Field label={ru.common.to} value={to} onChange={setTo} type="date" required />
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={reset}>
            {ru.analytics.reset}
          </Button>
        </div>

        <p className="text-xs text-muted">{ru.common.noPii}</p>
      </Card>

      <AsyncState
        isLoading={analyticsQuery.isLoading}
        isError={analyticsQuery.isError}
        onRetry={() => void analyticsQuery.refetch()}
      >
        {summary ? (
          <>
            <Card title={ru.analytics.title}>
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
                <Stat label={ru.analytics.visits} value={summary.visits} />
                <Stat label={ru.analytics.bookings} value={summary.bookings} />
                <Stat label={ru.analytics.cancellations} value={summary.cancellations} />
                <Stat label={ru.analytics.cancellationRate} value={formatPercent(summary.cancellationRate)} />
                <Stat label={ru.analytics.averageCheck} value={formatMoney(summary.averageCheck)} />
                <Stat label={ru.analytics.repeatRate} value={formatPercent(summary.repeatRate)} />
              </div>
            </Card>

            {breakdowns.map((breakdown) => (
              <Card key={breakdown.title} title={breakdown.title}>
                {breakdown.items.length === 0 ? (
                  <p className="text-sm text-muted">{ru.analytics.noData}</p>
                ) : (
                  <BreakdownList items={breakdown.items} />
                )}
              </Card>
            ))}
          </>
        ) : null}
      </AsyncState>
    </AppShell>
  );
}

function BreakdownList({ items }: { items: Array<{ key: string; label: string; value: number }> }) {
  const max = Math.max(...items.map((item) => item.value), 1);
  return (
    <ul className="flex flex-col gap-2">
      {items.map((item) => (
        <li key={item.key} className="flex flex-col gap-1">
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="truncate">{item.label}</span>
            <span className="font-mono">{item.value}</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-pill bg-ink-700">
            <div
              className="h-full bg-accent"
              style={{ width: `${Math.round((item.value / max) * 100)}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}
